import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, Search, X } from "lucide-react";
import { useInquiry } from "@/context/InquiryContext";
import useProducts from "@/hooks/useProducts";
import { SITE } from "@/lib/site";

const NAV = [
  { to: "/", label: "Home", id: "nav-link-home" },
  { to: "/about", label: "About Us", id: "nav-link-about" },
  { to: "/products", label: "Products", id: "nav-link-products" },
  { to: "/gallery", label: "Gallery", id: "nav-link-gallery" },
  { to: "/contact", label: "Contact Us", id: "nav-link-contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const { openQuote } = useInquiry();
  const { data: products } = useProducts();
  const navigate = useNavigate();
  const searchRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const results = (products || [])
    .filter((p) => `${p.name} ${p.tagline}`.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 6);

  const go = (slug) => {
    setSearchOpen(false);
    setQuery("");
    navigate(`/products/${slug}`);
  };

  const SearchBox = ({ big = false }) => (
    <div ref={searchRef} className="relative">
      <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        data-testid="nav-search-input"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setSearchOpen(true);
        }}
        onFocus={() => setSearchOpen(true)}
        placeholder="Search products…"
        className={`w-full rounded-full border border-slate-200 bg-slate-50 pl-9 pr-4 outline-none transition focus:border-rose-400 focus:bg-white focus:ring-2 focus:ring-rose-100 ${big ? "py-2.5 text-sm" : "py-2 text-xs"}`}
      />
      {query && searchOpen && (
        <div className="absolute inset-x-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          {results.length === 0 && <p className="px-4 py-3 text-xs text-slate-400">No matching products.</p>}
          {results.map((p) => (
            <button
              key={p.slug}
              type="button"
              onMouseDown={() => go(p.slug)}
              data-testid={`search-result-${p.slug}`}
              className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition hover:bg-rose-50"
            >
              <span className="text-sm font-medium text-slate-800">{p.name}</span>
              <span className="hidden font-mono text-[10px] uppercase tracking-wider text-slate-400 sm:block">
                {p.slug.replace(/-/g, " ")}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <header className="fixed inset-x-0 top-0 z-50 print:hidden">
      {/* Top bar */}
      <div className={`hidden bg-navy text-white transition-all md:block ${scrolled ? "h-0 overflow-hidden" : "h-10"}`}>
        <div className="mx-auto flex h-10 max-w-7xl items-center justify-between px-6 font-mono text-[11px] tracking-wider text-white/70">
          <div className="flex items-center gap-6">
            <a href={SITE.phoneHref} className="whitespace-nowrap transition hover:text-rose-300">PHONE {SITE.phoneDisplay}</a>
            <a href={`mailto:${SITE.email}`} className="transition hover:text-rose-300">{SITE.email}</a>
          </div>
          <p className="whitespace-nowrap uppercase">GSTIN 33AINPR0146B1ZM · Chennai · Est. 2006</p>
        </div>
      </div>

      {/* Main bar */}
      <div className={`transition-all duration-300 ${scrolled || mobileOpen ? "bg-white/95 shadow-md shadow-slate-900/5 backdrop-blur-xl" : "bg-white/85 backdrop-blur-md"} border-b border-slate-200/70`}>
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 lg:h-[72px] lg:gap-5 lg:px-6">
          {/* Logo */}
          <Link to="/" data-testid="header-logo" className="flex shrink-0 items-center gap-2.5">
            <img src="/img/ksc/logo.jpg" alt="KSC logo" className="h-11 w-11 shrink-0 rounded-lg object-cover" />
            <span className="leading-none">
              <span className="whitespace-nowrap font-display text-base font-extrabold tracking-tight text-slate-900 xl:text-lg">
                KRITHISH <span className="text-safety">SCIENTIFIC COMPANY</span>
              </span>
              <span className="mt-1 hidden whitespace-nowrap font-mono text-[8px] uppercase tracking-[0.22em] text-slate-500 xl:block">
                Precision in Every Scale
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-5 lg:flex xl:gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.id}
                className={({ isActive }) =>
                  `whitespace-nowrap text-sm font-medium transition ${isActive ? "text-safety" : "text-slate-600 hover:text-slate-900"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden w-40 items-center xl:flex xl:w-44">
            <SearchBox />
          </div>

          <div className="flex shrink-0 items-center gap-2.5">
            <a
              href={SITE.phoneHref}
              data-testid="header-phone-link"
              className="hidden items-center gap-2 whitespace-nowrap rounded-full border border-slate-200 px-3.5 py-2 font-mono text-xs font-semibold text-slate-700 transition hover:border-safety hover:text-safety lg:flex"
            >
              <Phone size={13} /> {SITE.phoneDisplay}
            </a>
            <button
              data-testid="nav-get-quote-btn"
              onClick={() => openQuote()}
              className="hidden whitespace-nowrap rounded-full bg-safety px-4 py-2 text-xs font-semibold text-white transition hover:bg-safety-dark sm:block"
            >
              Get Quote
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((o) => !o)}
              className="rounded-lg p-2 text-slate-700 transition hover:bg-slate-100 lg:hidden"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="overflow-hidden border-t border-slate-100 lg:hidden"
            >
              <div className="space-y-1 px-6 py-4">
                <SearchBox big />
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={n.id}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-2.5 text-sm font-medium transition ${isActive ? "bg-rose-50 text-safety" : "text-slate-700 hover:bg-slate-50"}`
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    openQuote();
                  }}
                  className="mt-2 w-full rounded-lg bg-safety px-4 py-3 text-sm font-semibold text-white"
                >
                  Get a Quote
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
